import React, { memo, useCallback, useState } from 'react';
import { Headline6, Paragraph4 } from '@nexus/lib-react/dist/core/Typography';
import { useI18n } from '../../../../i18n';
import S from './style/Dac7TabContent';
import V from './utils/validation';
import { SellerBusinessDac7 } from '../../../../../typings/dac7';
import Dac7Input from './components/Dac7Input';
import Dac7TaxDetails from './components/Dac7TaxDetails';
import Dac7PermanentEstablishment from './components/Dac7PermanentEstablishment';
import Dac7FinancialDetails from './components/Dac7FinancialDetails';
import Dac7ZipCodeField from './components/Dac7ZipCodeField';
import Dac7Dropdown from './components/Dac7Dropdown';
import Dac7Residencies from './components/Dac7Residencies';
import countries from './utils/countriesList';

interface Props {
  data: Readonly<SellerBusinessDac7>;
}

const DAC7ContentBusiness = ({ data }: Props) => {
  const i18n = useI18n();
  const [currentAddresses, setCurrentAddresses] = useState<Array<string>>([
    data.data.address?.countryCode || '',
  ]);
  const [taxCountries, setTaxCountries] = useState<Array<string>>(
    data.data.taxDetails.taxNumbersDetails.map(({ countryCode }) => countryCode),
  );
  const [establishmentCountries, setEstablishmentCountries] = useState<Array<string>>(
    data.data.address?.establishmentCountries.map(({ countryCode }) => countryCode) || [],
  );

  const onChangePermanentEstablishment = useCallback(
    (index, newValue) => {
      setEstablishmentCountries(prevEstablishmentCountries => {
        if (index < 0 || index >= prevEstablishmentCountries.length) {
          return prevEstablishmentCountries;
        }

        const updatedEstablishmentCountries = [...prevEstablishmentCountries];
        updatedEstablishmentCountries[index] = newValue;
        return updatedEstablishmentCountries;
      });
    },
    [setEstablishmentCountries],
  );

  return (
    <>
      {/* -------------------------------- Business Details Section -------------------------------- */}
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={8}>{i18n('dlv_dac7_businessDetails')}</Headline6>
        </legend>
        <Paragraph4 mb={24}>{i18n('dlv_dac7_businessDetailsDescription')}</Paragraph4>
        <S.Dac7Fieldset className="dac7-business-details">
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_businessDetails.companyName"
              label={i18n('dlv_dac7_legalName')}
              pattern={V.LEGAL_NAME_PATTERN}
              required
              value={data.data.businessDetails.companyName}
              disabled={!!data.frozenData.businessDetails.companyName}
            />
          </S.Dac7FormField>
        </S.Dac7Fieldset>
        <S.StyledRequiredFieldsText>
          {i18n(`dlv_dac7_required_fields_description`)}
        </S.StyledRequiredFieldsText>
      </S.Dac7TabContentWrapper>
      <S.StyledInvisibleDivider />

      {/* -------------------------------- Address Section -------------------------------- */}
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={8}>{i18n('dlv_dac7_business_address')}</Headline6>
        </legend>
        <Paragraph4 mb={24}>{i18n('dlv_dac7_businessAddressDescription')}</Paragraph4>
        <S.Dac7Fieldset className="dac7-address">
          <S.Dac7FormField>
            <Dac7Dropdown
              sourceItems={countries}
              initialSelectedIds={[data.data.address?.countryCode]}
              name="dac7_address.countryCode"
              label={i18n('dlv_dac7_country')}
              required
              requiredMessage={i18n('dlv_dac7_address.countryRequired')}
              placeholder={i18n('dlv_dac7_countryPlaceholder')}
              disabled={!!data.frozenData.address?.countryCode}
              onChange={codes => setCurrentAddresses(codes.map(code => String(code)))}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_address.streetName"
              label={i18n('dlv_dac7_street')}
              pattern={V.getMinAlphaNumericSymbolPattern(2)}
              required
              value={data.data.address?.streetName || ''}
              disabled={!!data.frozenData.address?.streetName}
            />
          </S.Dac7FormField>
          <S.Dac7HouseNumberApartmentContainer>
            <S.Dac7FormField>
              <Dac7Input
                name="dac7_address.streetNumber"
                label={i18n('dlv_dac7_houseNumber')}
                pattern={V.getMinAlphaNumericSymbolPattern(1)}
                required
                value={data.data.address?.streetNumber || ''}
                disabled={!!data.frozenData.address?.streetNumber}
              />
            </S.Dac7FormField>
            <S.Dac7FormField>
              <Dac7Input
                name="dac7_address.officeNumber"
                label={i18n('dlv_dac7_officeNumber')}
                value={data.data.address?.officeNumber || ''}
                disabled={
                  !!data.frozenData.address?.officeNumber || !!data.frozenData.address?.streetName
                }
              />
            </S.Dac7FormField>
          </S.Dac7HouseNumberApartmentContainer>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_address.cityName"
              label={i18n('dlv_dac7_city')}
              pattern={V.CITY_PATTERN}
              required
              value={data.data.address?.cityName || ''}
              disabled={!!data.frozenData.address?.cityName}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7ZipCodeField
              name="dac7_address.postCode"
              label={i18n('dlv_dac7_zipCode')}
              required
              value={data.data.address?.postCode || ''}
              disabled={!!data.frozenData.address?.postCode}
            />
          </S.Dac7FormField>
          <Dac7PermanentEstablishment
            permanentEstablishmentCountries={data.data.address.establishmentCountries}
            frozenPermanentEstablishmentCountries={data.frozenData.address.establishmentCountries}
            onChange={onChangePermanentEstablishment}
          />
        </S.Dac7Fieldset>
        <S.StyledRequiredFieldsText>
          {i18n(`dlv_dac7_required_fields_description`)}
        </S.StyledRequiredFieldsText>
      </S.Dac7TabContentWrapper>
      <S.StyledInvisibleDivider />

      {/* -------------------------------- Tax Details Section -------------------------------- */}
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={8}>{i18n('dlv_dac7_taxDetails')}</Headline6>
        </legend>
        <Paragraph4 mb={24}>{i18n('dlv_dac7_businessTaxDescription')}</Paragraph4>
        <Dac7TaxDetails
          taxDetails={data.data.taxDetails.taxNumbersDetails}
          frozenTaxDetails={data.frozenData.taxDetails.taxNumbersDetails}
          onAdd={() => {
            setTaxCountries(prevTaxCountries => [...prevTaxCountries, '']);
          }}
          onRemove={index => {
            setTaxCountries(prevTaxCountries => [
              ...prevTaxCountries.slice(0, index),
              ...prevTaxCountries.slice(index + 1),
            ]);
          }}
          onChange={(index, newValue) => {
            setTaxCountries(prevTaxCountries => {
              if (index < 0 || index >= prevTaxCountries.length) {
                return prevTaxCountries;
              }

              const updatedTaxCountries = [...prevTaxCountries];
              updatedTaxCountries[index] = newValue;
              return updatedTaxCountries;
            });
          }}
        />
        <S.Dac7Fieldset>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_taxDetails.vatNumber"
              label={i18n('dlv_dac7_taxDetails_vat')}
              pattern={V.getMinLettersOrNumbersPattern(2)}
              value={data.data.taxDetails.vatNumber}
              disabled={!!data.frozenData.taxDetails.vatNumber}
            />
          </S.Dac7FormField>
          <S.Dac7FormField>
            <Dac7Input
              name="dac7_taxDetails.businessRegistrationNumber"
              label={i18n('dlv_dac7_taxDetails_businessRegistrationNumber')}
              pattern={V.getMinLettersOrNumbersPattern(2)}
              value={data.data.taxDetails.businessRegistrationNumber}
              disabled={!!data.frozenData.taxDetails.businessRegistrationNumber}
            />
          </S.Dac7FormField>
        </S.Dac7Fieldset>
        <S.StyledRequiredFieldsText>
          {i18n(`dlv_dac7_required_fields_description`)}
        </S.StyledRequiredFieldsText>
      </S.Dac7TabContentWrapper>
      <S.StyledInvisibleDivider />

      {/* -------------------------------- Residencies Section -------------------------------- */}
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={8}>{i18n('dlv_dac7_residency_business')}</Headline6>
        </legend>
        <Paragraph4 mb={24}>{i18n('dlv_dac7_residency_business_description')}</Paragraph4>
        <Dac7Residencies
          countryCodes={[
            ...data.data.residencies?.map(({ countryCode }) => countryCode || ''),
            ...currentAddresses,
            ...establishmentCountries,
            ...taxCountries,
          ]}
        />
      </S.Dac7TabContentWrapper>

      {/* -------------------------------- Financial Details Section -------------------------------- */}
      <S.Dac7TabContentWrapper>
        <legend>
          <Headline6 mb={8}>{i18n('dlv_dac7_financialDetails')}</Headline6>
        </legend>
        <Paragraph4 mb={24}>{i18n('dlv_dac7_businessFinancialDescription')}</Paragraph4>
        <Dac7FinancialDetails
          financialDetails={data.data.financialDetails}
          frozenFinancialDetails={data.frozenData.financialDetails}
        />
      </S.Dac7TabContentWrapper>
    </>
  );
};

export default memo(DAC7ContentBusiness);
